/**
 * SyncResultPanel — WSPÓLNY widok wyniku synchronizacji ERP dla wszystkich okien
 * (import / eksport / synchronizacja dwukierunkowa / automatyzacje).
 *
 * Renderuje siatkę KLIKALNYCH kafelków (podświetlenie wybranego) + obszar drill-downu o
 * STAŁEJ wysokości (nie skacze przy zmianie kubełka / liczby rekordów) z wewnętrznym scrollem.
 * Po kliknięciu kafelka pod spodem pokazuje listę rekordów tego kubełka: nazwa (opcjonalnie
 * link do kartoteki), NIP, nr w ERP, powód (pominięte/wykluczone), zmienione pola „było → jest"
 * (zaktualizowane). Pusty kubełek → „brak rekordów"; nic nie wybrane → „kliknij kafelek".
 *
 * Etykiety kafelków przekazuje wołający (każde okno ma własne nazwy kubełków); wspólne teksty
 * (NIP, nr w ERP, „brak rekordów", „kliknij kafelek", „bez zmian pól") żyją tu, w namespace
 * `tenant.settings.integrations.syncResult`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { cn } from '@/lib/utils';

/** Jeden rekord w kubełku wyniku — lustro backendowego `SyncRecordDetail`. */
export interface SyncRecordDetail {
  /** Id rekordu u nas — jeśli podane i `linkBase` ustawione, nazwa jest linkiem do kartoteki. */
  id?: string;
  /** Etykieta rekordu (nazwa kontrahenta / pracownika). */
  record: string;
  /** NIP/identyfikator pomocniczy — rozróżnia rekordy o tej samej nazwie. */
  taxId?: string | null;
  /** Id rekordu po stronie ERP (nadany/dopasowany). */
  externalId?: string;
  /** Powód — dla pominiętych/wykluczonych/błędów. */
  reason?: string;
  /**
   * Zmienione pola — dla zaktualizowanych: etykieta + było → jest. Pusta tablica = rekord
   * przetworzony, ale bez zmian pól (pokazujemy „bez zmian pól"); `undefined` = brak sekcji.
   */
  fields?: Array<{ field: string; from: string; to: string }>;
}

/** Ton kolorystyczny kafelka (kolor liczby + obwódki po zaznaczeniu). */
export type SyncTileTone = 'added' | 'updated' | 'neutral' | 'warning' | 'danger' | 'muted';

export interface SyncResultTile {
  /** Klucz techniczny (unikalny w panelu) — trafia do `data-testid`. */
  key: string;
  /** Etykieta (już przetłumaczona przez wołającego). */
  label: string;
  count: number;
  tone?: SyncTileTone;
  /** Rekordy kubełka do drill-downu. `undefined` = kafelek bez rozwijania (sam licznik). */
  records?: SyncRecordDetail[];
}

const TONE: Record<SyncTileTone, { num: string; ring: string }> = {
  added: { num: 'text-green-600', ring: 'ring-green-500' },
  updated: { num: 'text-accent-500', ring: 'ring-accent-500' },
  neutral: { num: 'text-text-primary', ring: 'ring-accent-500' },
  warning: { num: 'text-yellow-600', ring: 'ring-yellow-500' },
  danger: { num: 'text-red-600', ring: 'ring-red-500' },
  muted: { num: 'text-text-tertiary', ring: 'ring-border-primary' },
};

const PREFIX = 'tenant.settings.integrations.syncResult';

export function SyncResultPanel({
  tiles,
  onNavigate,
  linkBase,
  testId = 'sync-result',
  listHeightClass = 'h-56',
  fillHeight = false,
}: {
  tiles: SyncResultTile[];
  /** Wołane przy kliknięciu linku do kartoteki (np. zamknięcie modala). */
  onNavigate?: () => void;
  /** Baza ścieżki kartoteki (np. `/contractors`, `/employees`). Bez niej nazwy nie są linkami. */
  linkBase?: string;
  /**
   * DEPRECATED — kafelki są teraz zwartym paskiem pigułek (flex-wrap), same się układają w rzędy.
   * Prop zostaje dla zgodności wywołań; nie wpływa już na render.
   */
  columns?: 3 | 4;
  /** Prefiks `data-testid` (root/kafelki/lista) — pozwala odróżnić panele w tym samym drzewie. */
  testId?: string;
  /** Klasa wysokości obszaru listy (stała, ze scrollem). Ignorowana gdy `fillHeight`. */
  listHeightClass?: string;
  /**
   * Tryb „wypełnij wysokość": panel rozciąga się na całą dostępną wysokość rodzica (flex column),
   * kafelki są stałe, a scrolluje się WYŁĄCZNIE lista logów — dzięki temu modal ma JEDEN scroll
   * (listy), nie dwa (modal + lista). Rodzic musi dać panelowi ograniczoną wysokość
   * (`flex-1 min-h-0` w kolumnie flex). Bez flagi: dawne zachowanie ze stałą `listHeightClass`.
   */
  fillHeight?: boolean;
}) {
  const { t } = useTranslation();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = tiles.find((x) => x.key === selectedKey) ?? null;
  const rows = selected?.records ?? [];
  // Gdy ŻADEN kafelek nie ma drill-downu (np. słowniki — liczniki danych referencyjnych, nie
  // rekordy kartotek), nie pokazujemy w ogóle obszaru listy: byłby martwy („Kliknij kafelek",
  // a nic nie jest klikalne). Same pigułki-liczniki.
  const anyExpandable = tiles.some((x) => x.records !== undefined);

  return (
    <div
      className={cn(fillHeight ? 'flex h-full min-h-0 flex-col gap-2' : 'space-y-2')}
      data-testid={testId}
    >
      {/* Pasek pigułek (liczba + etykieta w jednej linii) — zwarty, sam się zawija w rzędy,
          zostawia maksimum miejsca na listę logów. Klik pigułki rozwija jej kubełek niżej. */}
      <div className={cn('flex flex-wrap gap-1.5', fillHeight && 'shrink-0')}>
        {tiles.map((tile) => {
          const tone = TONE[tile.tone ?? 'muted'];
          const active = selectedKey === tile.key;
          const canExpand = tile.records !== undefined;
          return (
            <Button
              key={tile.key}
              variant="ghost"
              disabled={!canExpand}
              onClick={() => setSelectedKey(active ? null : tile.key)}
              aria-pressed={active}
              data-testid={`${testId}-tile-${tile.key}`}
              className={cn(
                'flex h-auto items-baseline gap-1.5 rounded-full border border-border-primary bg-bg-secondary px-3 py-1.5 disabled:opacity-100',
                canExpand ? 'cursor-pointer' : 'cursor-default',
                active && cn('border-transparent bg-bg-tertiary ring-2', tone.ring),
              )}
            >
              <span className={cn('text-sm font-bold', tone.num)}>{tile.count}</span>
              <span className="text-xs text-text-tertiary">{tile.label}</span>
            </Button>
          );
        })}
      </div>

      {/* Scroll wewnętrzny → okno NIE skacze przy zmianie kubełka. W trybie `fillHeight` lista
          wypełnia resztę wysokości i jest JEDYNYM scrollem modala; inaczej stała wysokość.
          Ukryte całkowicie, gdy nic nie ma drill-downu (słowniki — same pigułki-liczniki). */}
      {anyExpandable && (
        <div
          className={cn(
            // Bez górnego paddingu → sticky-nagłówek tabeli (szukajki) przykleja się do samej góry
            // kontenera i zakrywa treść przewijaną pod spodem (koniec z „prześwitem" wiersza nad
            // szukajkami). Górny odstęp daje samej tabeli jej sticky `thead` (pt-3 w komórkach).
            'overflow-y-auto rounded-md border border-border-primary bg-bg-secondary px-3 pb-3',
            fillHeight ? 'min-h-0 flex-1' : listHeightClass,
          )}
          data-testid={`${testId}-bucket-${selected?.key ?? 'none'}`}
        >
          {!selected ? (
            <div className="pt-3 text-xs text-text-tertiary">{t(`${PREFIX}.pickTile`)}</div>
          ) : rows.length === 0 ? (
            <div className="pt-3 text-xs text-text-tertiary">{t(`${PREFIX}.empty`)}</div>
          ) : (
            // key=selected.key → filtry resetują się przy zmianie kubełka (remount).
            <BucketTable
              key={selected.key}
              rows={rows}
              linkBase={linkBase}
              onNavigate={onNavigate}
              testId={testId}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Kolumny tabelki rekordów — klucz filtra + etykieta i18n. */
const TABLE_COLUMNS: Array<{ key: 'name' | 'taxId' | 'externalId' | 'details'; labelKey: string }> =
  [
    { key: 'name', labelKey: 'colName' },
    { key: 'taxId', labelKey: 'colTaxId' },
    { key: 'externalId', labelKey: 'colFk' },
    { key: 'details', labelKey: 'colDetails' },
  ];

/** Normalizacja do filtrowania: lower-case + bez polskich znaków diakrytycznych. */
function normalizeForSearch(v: unknown): string {
  return (v ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Tekst kolumny „Szczegóły": powód (pominięte/wykluczone) albo zmiany pól było→jest. */
function detailsText(r: SyncRecordDetail): string {
  if (r.reason) return r.reason;
  if (r.fields && r.fields.length > 0) {
    return r.fields.map((f) => `${f.field}: ${f.from || '—'} → ${f.to || '—'}`).join('; ');
  }
  return '';
}

/**
 * Tabelka rekordów kubełka z WYSZUKIWARKĄ NAD KAŻDĄ KOLUMNĄ (filtr AND). Nazwa =
 * link do kartoteki (gdy `linkBase` + `id`). Nagłówek przyklejony przy scrollu.
 */
function BucketTable({
  rows,
  linkBase,
  onNavigate,
  testId,
}: {
  rows: SyncRecordDetail[];
  linkBase?: string;
  onNavigate?: () => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<
    Record<'name' | 'taxId' | 'externalId' | 'details', string>
  >({ name: '', taxId: '', externalId: '', details: '' });
  const matches = (value: unknown, query: string) =>
    !query || normalizeForSearch(value).includes(normalizeForSearch(query));
  const filtered = rows.filter(
    (r) =>
      matches(r.record, filters.name) &&
      matches(r.taxId, filters.taxId) &&
      matches(r.externalId, filters.externalId) &&
      matches(detailsText(r), filters.details),
  );

  return (
    <table className="w-full text-xs" data-testid={`${testId}-table`}>
      <thead className="sticky top-0 z-10 bg-bg-secondary">
        <tr className="text-left text-text-tertiary">
          {TABLE_COLUMNS.map((c) => (
            <th key={c.key} className="pb-1 pr-2 pt-3 font-medium">
              {t(`${PREFIX}.${c.labelKey}`)}
            </th>
          ))}
        </tr>
        <tr>
          {TABLE_COLUMNS.map((c) => (
            <th key={c.key} className="pb-2 pr-2 align-top font-normal">
              <Input
                value={filters[c.key]}
                onChange={(e) => setFilters((s) => ({ ...s, [c.key]: e.target.value }))}
                placeholder={t(`${PREFIX}.filterPlaceholder`)}
                className="h-7 py-1 text-xs"
                data-testid={`${testId}-filter-${c.key}`}
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="text-text-secondary">
        {filtered.length === 0 ? (
          <tr>
            <td colSpan={TABLE_COLUMNS.length} className="py-3 text-center text-text-tertiary">
              {t(`${PREFIX}.noMatch`)}
            </td>
          </tr>
        ) : (
          filtered.map((r, i) => (
            <tr key={r.id ?? i} className="border-t border-border-primary/40 align-top">
              <td className="py-1 pr-2 font-medium">
                {linkBase && r.id ? (
                  <Link
                    to={`${linkBase}/${r.id}`}
                    onClick={onNavigate}
                    className="text-accent-600 hover:underline dark:text-accent-400"
                  >
                    {r.record}
                  </Link>
                ) : (
                  <span className="text-text-primary">{r.record}</span>
                )}
              </td>
              <td className="py-1 pr-2 text-text-tertiary">{r.taxId || '—'}</td>
              <td className="py-1 pr-2 text-text-tertiary">{r.externalId || '—'}</td>
              <td className="py-1">
                {r.fields && r.fields.length > 0 ? (
                  <ul className="space-y-0.5">
                    {r.fields.map((f, j) => (
                      <li key={j} className="font-mono">
                        {f.field}: <span className="text-text-tertiary">{f.from || '—'}</span> →{' '}
                        <span className="text-text-primary">{f.to || '—'}</span>
                      </li>
                    ))}
                  </ul>
                ) : r.reason ? (
                  <span className="text-text-tertiary">{r.reason}</span>
                ) : r.fields ? (
                  <span className="text-text-tertiary">{t(`${PREFIX}.noFieldChanges`)}</span>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
